import { useQuery } from '@apollo/client';
import { AlertCircle } from 'lucide-react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { DelayedSkeleton, DetailsWithListsSkeleton } from '@/components/ui/delayed-skeleton';

import { CONTRACT_DETAIL_QUERY } from './contracts.gql';
import { ContractDetails } from './contract-details';
import { ContractListItem } from './types';
import { computeContractStatus } from './utils';

interface ContractDetailsQueryProps {
        contractId: string;
        showHeader?: boolean;
        onEdit?: (id: string) => void;
        onClone?: (id: string) => void;
        onDelete?: (id: string) => void;
        onClose?: () => void;
        onBack?: () => void;
        showBackButton?: boolean;
}

interface ContractDetailNode {
        contractId: string;
        contractNumber?: string | null;
        beginDate: string;
        endDate: string;
        amount?: number | null;
        isAutoRenewed: boolean;
        simultaneousVehicleLimit?: number | null;
        reservedParkingSlots?: number | null;
        preferredLanguage?: string | null;
        isActive: boolean;
        customer?: {
                customerId: string;
                firmName?: string | null;
                firstName?: string | null;
                lastName?: string | null;
                isCompany?: boolean | null;
                customerCategory?: {
                        description?: string | null;
                        simultaneousVehicleLimit?: number | null;
                } | null;
                vehicles?: Array<{
                        vehicleId: string;
                        plateNumber?: string | null;
                        vehicleType?: {
                                name?: string | null;
                        } | null;
                        make?: string | null;
                        model?: string | null;
                        color?: string | null;
                }> | null;
        } | null;
        includeAllCustomerVehicles: boolean;
        vehicles?: Array<{
                contractId: string;
                vehicleId: string;
                vehicle?: {
                        plateNumber?: string | null;
                        vehicleType?: {
                                name?: string | null;
                        } | null;
                        make?: string | null;
                        model?: string | null;
                        color?: string | null;
                } | null;
        }> | null;
        vipVehicles?: string[] | null;
        cards?: Array<{
                contractCardId?: string | null;
                facilityId: string;
                cardType: string;
                cardKey?: string | null;
                cardStatusId: string;
                depositTransactionId?: string | null;
                returnTransactionId?: string | null;
                depositTransaction?: {
                        grossAmount?: number | null;
                } | null;
        }> | null;
        parkingSessions?: Array<{
                parkingSessionId: string;
                entryDateTime: string;
                exitDateTime?: string | null;
                status: string;
        }> | null;
}

interface ContractDetailQueryResponse {
        contract?: ContractDetailNode | null;
}

const mapNodeToListItem = (node: ContractDetailNode): ContractListItem => {
        return {
                contractId: node.contractId,
                contractNumber: node.contractNumber ?? null,
                customer: {
                        customerId: node.customer?.customerId ?? 'unknown-customer',
                        firmName: node.customer?.firmName ?? null,
                        firstName: node.customer?.firstName ?? null,
                        lastName: node.customer?.lastName ?? null,
                        isCompany: node.customer?.isCompany ?? null,
                        categoryName: node.customer?.customerCategory?.description ?? undefined,
                        categoryLimit: node.customer?.customerCategory?.simultaneousVehicleLimit ?? undefined,
                        contactName: undefined,
                        contactEmail: undefined,
                        contactPhone: undefined,
                },
                beginDate: node.beginDate,
                endDate: node.endDate,
                amount: (() => {
                        if (typeof node.amount === 'number') {
                                return node.amount;
                        }
                        if (typeof node.amount === 'string') {
                                const parsed = Number(node.amount);
                                return Number.isNaN(parsed) ? null : parsed;
                        }
                        return null;
                })(),
                billingCycle: undefined, // Not available in current schema
                isAutoRenewed: node.isAutoRenewed,
                simultaneousVehicleLimit: node.simultaneousVehicleLimit ?? undefined,
                reservedParkingSlots: node.reservedParkingSlots ?? undefined,
                preferredLanguage: node.preferredLanguage ?? undefined,
                isActive: node.isActive,
                includeAllCustomerVehicles: node.includeAllCustomerVehicles,
                vehicles: node.vehicles?.map(vehicle => ({
                        vehicleId: vehicle.vehicleId,
                        plateNumber: vehicle.vehicle?.plateNumber ?? null,
                        vehicleTypeName: vehicle.vehicle?.vehicleType?.name ?? undefined,
                        make: vehicle.vehicle?.make ?? undefined,
                        model: vehicle.vehicle?.model ?? undefined,
                        color: vehicle.vehicle?.color ?? undefined,
                })) ?? [],
                vipVehicleIds: node.vipVehicles ?? [],
                vipVehicles: (node.vipVehicles ?? []).map(vipId => {
                        // Try to resolve from contract vehicles first, then from customer vehicles
                        const contractVehicle = node.vehicles?.find(v => v.vehicleId === vipId);
                        if (contractVehicle) {
                                return {
                                        vehicleId: contractVehicle.vehicleId,
                                        plateNumber: contractVehicle.vehicle?.plateNumber ?? null,
                                        vehicleTypeName: contractVehicle.vehicle?.vehicleType?.name ?? undefined,
                                        make: contractVehicle.vehicle?.make ?? undefined,
                                        model: contractVehicle.vehicle?.model ?? undefined,
                                        color: contractVehicle.vehicle?.color ?? undefined,
                                };
                        }
                        const customerVehicle = node.customer?.vehicles?.find(v => v.vehicleId === vipId);
                        if (customerVehicle) {
                                return {
                                        vehicleId: customerVehicle.vehicleId,
                                        plateNumber: customerVehicle.plateNumber ?? null,
                                        vehicleTypeName: customerVehicle.vehicleType?.name ?? undefined,
                                        make: customerVehicle.make ?? undefined,
                                        model: customerVehicle.model ?? undefined,
                                        color: customerVehicle.color ?? undefined,
                                };
                        }
                        // Fallback: return minimal info with just the ID
                        return {
                                vehicleId: vipId,
                                plateNumber: null,
                                vehicleTypeName: undefined,
                                make: undefined,
                                model: undefined,
                                color: undefined,
                        };
                }),
                cards: node.cards?.map(card => ({
                        contractCardId: card.contractCardId ?? undefined,
                        facilityId: card.facilityId,
                        cardType: card.cardType as 'RFID' | 'BARCODE' | 'MAGNETIC',
                        cardKey: card.cardKey ?? undefined,
                        cardStatusId: card.cardStatusId,
                        depositTransactionId: card.depositTransactionId ?? undefined,
                        returnTransactionId: card.returnTransactionId ?? undefined,
                        depositAmount: card.depositTransaction?.grossAmount ?? undefined,
                })) ?? [],
                metrics: {
                        activeParkingSessions: node.parkingSessions?.filter(session => session.status === 'INSIDE').length ?? 0,
                        upcomingInvoiceDate: null,
                        overstayIncidents: 0,
                        lastAuditEvent: null
                },
                notes: [],
                status: computeContractStatus({
                        beginDate: node.beginDate,
                        endDate: node.endDate,
                        isActive: node.isActive
                })
        };
};

export const ContractDetailsQuery = ({
	contractId,
	showHeader,
	onEdit,
	onClone,
	onDelete,
	onClose,
	onBack,
	showBackButton
}: ContractDetailsQueryProps) => {
        const { data, loading, error, refetch } = useQuery<ContractDetailQueryResponse>(
                CONTRACT_DETAIL_QUERY,
                {
                        variables: { contractId },
                        fetchPolicy: 'cache-and-network',
                        errorPolicy: 'all'
                }
        );

        if (error && !data) {
                return (
                        <Alert variant="destructive">
                                <AlertCircle className="h-4 w-4" />
                                <AlertDescription>
                                        Unable to load contract details. Please try again.
                                </AlertDescription>
                        </Alert>
                );
        }

        const contractNode = data?.contract;

        return (
                <DelayedSkeleton
                        isLoading={loading}
                        delay={300}
                        showInitialLoading={false}
                        fallback={<DetailsWithListsSkeleton sections={2} listCount={2} />}
                >
                        {contractNode ? (
                                <ContractDetails
                                        contract={mapNodeToListItem(contractNode)}
                                        showHeader={showHeader}
                                        onEdit={onEdit}
                                        onClone={onClone}
                                        onDelete={onDelete}
                                        onClose={onClose}
                                        onBack={onBack}
                                        showBackButton={showBackButton}
                                        onContractRefresh={refetch}
                                />
                        ) : (
                                <Alert>
                                        <AlertCircle className="h-4 w-4" />
                                        <AlertDescription>
                                                Contract not found.
                                        </AlertDescription>
                                </Alert>
                        )}
                </DelayedSkeleton>
        );
};
